import buildClientAPI from 'views/providers/clientAPI'
import experimentsToManuallyActivate from 'shared/experiments/experimentsToManuallyActivate'
import store from 'shared/store'

import {
  ECONDA_SESSION_ID_COOKIE,
  ECONDA_VISITOR_ID_COOKIE
} from 'shared/consts/econdaCookieIds'

import { getCookieByName } from 'shared/utils/getCookie'

import { DEFAULT_VARIANT_NAME } from 'shared/consts/optimizely'

export const getExperimentsToActivate = ({
  includeExperimentsWithPreventActivation = false
} = {}) => {
  const {
    pageProperties: { experiments = [] }
  } = store.getPublicRuntimeConfig()

  // We only activate the features from experiments
  // If a feature has variant variable, it is from an experiment
  const activeExperimentsInCookie = experiments
    .filter(exp => exp?.variant)
    .map(exp => exp?.name)
  // eslint-disable-next-line max-params
  const unique = (exp, i, self) => self.indexOf(exp) === i
  const includedInCookie = exp => activeExperimentsInCookie.includes(exp)
  // Experiments wrapped in Experiment component will be caught with this.
  // We do it this way b/c we don't want to activate all features but just the ones
  // that are rendered for that user and ones that we've manually setup to be
  // activated (ones comming from experimentsToManuallyActivate)
  const experimentsInDOM = [
    ...document.querySelectorAll(
      includeExperimentsWithPreventActivation
        ? '[data-experiment]'
        : '[data-experiment]:not([data-prevent-activation])'
    )
  ].map(({ dataset: { experiment } }) => experiment)
  const experimentsToActivate = [
    ...experimentsToManuallyActivate,
    ...experimentsInDOM
  ]
    // only activate these which are also present in the cookie
    .filter(includedInCookie)
    // safety to prevent multiple activation if there are same epxeriments in both lists somehow
    .filter(unique)

  if (!experimentsToActivate?.length) return []

  return experimentsToActivate
}

export const activateExperiment = name => {
  const { clientFockService } = buildClientAPI()
  clientFockService.activateExperiments([name])
}

export const activateExperiments = () => {
  const experimentsToActivate = getExperimentsToActivate()

  if (experimentsToActivate?.length) {
    const { clientFockService } = buildClientAPI()
    clientFockService.activateExperiments(experimentsToActivate)
  }
}

// For use in case of hydrated react components, client
// scripts or when you know what needs to be triggered but can't implement
// using Track component for some reason (ie event that is different then click).
// eslint-disable-next-line max-params
export const trackEvent = (eventName, eventTags = {}) => {
  const { clientFockService } = buildClientAPI()

  const { cookie = '' } = document

  const econdaSessionId = getCookieByName(
    ECONDA_SESSION_ID_COOKIE,
    cookie
  )?.split(':')[0]

  const econdaVisitorId = getCookieByName(
    ECONDA_VISITOR_ID_COOKIE,
    cookie
  )?.split(':')[0]

  const eventTagsWithEcondaIds = {
    ...eventTags,
    econdaSessionId,
    econdaVisitorId
  }

  clientFockService.trackExperiment(eventName, eventTagsWithEcondaIds)
}

export const startTrackingExperiments = () => {
  ;[...document.querySelectorAll('[data-tracking-event]')].forEach(el => {
    el.addEventListener('click', function injectedTrackingEvent() {
      // TODO: see about propagation, bubbling and everything necessary so
      // that this event doesn't affect inner ones
      const { trackingEvent: event, trackingData: data } = el.dataset
      trackEvent(event, data)
    })
  })
}

export const isExperimentEnabled = nameToFind => {
  const experimentsToActivate = getExperimentsToActivate({
    includeExperimentsWithPreventActivation: true
  })
  return experimentsToActivate.some(name => name === nameToFind)
}

export const experimentIsActiveAndHasNonDefaultVariant = (name, experiments) =>
  experiments.some(
    experiment =>
      experiment.name === name && experiment.variant !== DEFAULT_VARIANT_NAME
  )
